<script setup lang="ts">
import type { IconKeys } from "@/components/ui/Icon/Icon.interface"
import type { EventVault } from "@/models"
import { useCycleList } from "@vueuse/core"
import { DialogClose } from "reka-ui"
import { computed, watch } from "vue"
import FreemiumUpgradeCard from "@/components/FreemiumUpgradeCard/FreemiumUpgradeCard.vue"
import { Alert, Button, Icon } from "@/components/ui"
import BaseModalReka from "@/components/ui/BaseModalReka/BaseModalReka.vue"
import { useTranslation } from "@/composables"

interface OnboardingGuideModalProps {
  open: boolean
  eventVault?: EventVault
}

interface OnboardingSlide {
  key: string
  icon: IconKeys
  title: string
  description: string
  bulletsLabel?: string
  bullets?: string[]
  tip?: string
  showUpgradeCard?: boolean
}

const props = defineProps<OnboardingGuideModalProps>()

const emit = defineEmits<{
  "update:open": [value: boolean]
}>()

const { t } = useTranslation()

const FREEMIUM_UPLOAD_WINDOW_DAYS = 30

const settings = computed(() => props.eventVault?.eventVaultSettings)

const isFreemium = computed(() => props.eventVault?.freemiumMode ?? false)
const canCustomizeTheme = computed(() => Boolean(settings.value?.allowThemeEdit || settings.value?.albumsEnabled || settings.value?.allowEventTemplate))
const canUseAlbums = computed(() => Boolean(settings.value?.albumsEnabled))

const freemiumIncludedItems = computed(() => [
  t("freemium_welcome_modal.included_items.photo_uploads", { uploads: props.eventVault?.maxPhotoLimit ?? 50 }),
  t("freemium_welcome_modal.included_items.upload_window", { days: FREEMIUM_UPLOAD_WINDOW_DAYS }),
  t("freemium_welcome_modal.included_items.default_colors_fonts"),
])

const shareBullets = computed(() => {
  const bullets = [
    t("onboarding_guide_modal.share.bullets.event_links"),
    t("onboarding_guide_modal.share.bullets.canva"),
    t("onboarding_guide_modal.share.bullets.share_directly"),
  ]

  return bullets
})

const customizeBullets = computed(() => {
  const bullets = [
    t("onboarding_guide_modal.customize.bullets.welcome_message"),
    t("onboarding_guide_modal.customize.bullets.settings"),
    t("onboarding_guide_modal.customize.bullets.media"),
  ]

  if (canCustomizeTheme.value)
    bullets.push(t("onboarding_guide_modal.customize.bullets.theme"))

  if (canUseAlbums.value)
    bullets.push(t("onboarding_guide_modal.customize.bullets.albums"))

  return bullets
})

const nextStepsBullets = computed(() => {
  const bullets = [
    t("onboarding_guide_modal.next_steps.bullets.share"),
    t("onboarding_guide_modal.next_steps.bullets.export"),
  ]

  if (isFreemium.value)
    bullets.push(t("onboarding_guide_modal.next_steps.bullets.upgrade"))

  return bullets
})

const slides = computed<OnboardingSlide[]>(() => {
  const welcomeSlide: OnboardingSlide = isFreemium.value
    ? {
        key: "welcome",
        icon: "CalendarCheck",
        title: t("freemium_welcome_modal.title"),
        description: t("freemium_welcome_modal.description"),
        bulletsLabel: t("freemium_welcome_modal.includes_label"),
        bullets: freemiumIncludedItems.value,
        showUpgradeCard: true,
      }
    : {
        key: "welcome",
        icon: "CalendarCheck",
        title: t("onboarding_guide_modal.welcome.title"),
        description: t("onboarding_guide_modal.welcome.description"),
      }

  return [
    welcomeSlide,
    {
      key: "share",
      icon: "QrCode",
      title: t("onboarding_guide_modal.share.title"),
      description: t("onboarding_guide_modal.share.description"),
      tip: t("onboarding_guide_modal.share.tip"),
      bullets: shareBullets.value,
    },
    {
      key: "collect",
      icon: "Mail",
      title: t("onboarding_guide_modal.collect.title"),
      description: t("onboarding_guide_modal.collect.description"),
      bullets: [
        t("onboarding_guide_modal.collect.bullets.share_details"),
        t("onboarding_guide_modal.collect.bullets.rsvp_responses"),
        t("onboarding_guide_modal.collect.bullets.manage_dashboard"),
      ],
    },
    {
      key: "customize",
      icon: "SettingsCog",
      title: t("onboarding_guide_modal.customize.title"),
      description: t("onboarding_guide_modal.customize.description"),
      bullets: customizeBullets.value,
    },
    {
      key: "next-steps",
      icon: "Grid",
      title: t("onboarding_guide_modal.next_steps.title"),
      description: t("onboarding_guide_modal.next_steps.description"),
      bullets: nextStepsBullets.value,
      showUpgradeCard: isFreemium.value,
    },
  ]
})

const {
  state: currentSlide,
  index: currentIndex,
  next: cycleNext,
  prev: cyclePrev,
  go: cycleGo,
} = useCycleList(slides, {
  getIndexOf: (value, list) => list.findIndex(slide => slide.key === value.key),
})

const isFirstSlide = computed(() => currentIndex.value === 0)
const isLastSlide = computed(() => currentIndex.value === slides.value.length - 1)
const progressPercent = computed(() => ((currentIndex.value + 1) / slides.value.length) * 100)

const stepLabel = computed(() => t("onboarding_guide_modal.step_of", {
  current: currentIndex.value + 1,
  total: slides.value.length,
}))

const stepAriaLabel = computed(() => t("onboarding_guide_modal.step_aria_label", {
  current: currentIndex.value + 1,
  total: slides.value.length,
}))

function close() {
  emit("update:open", false)
}

function onOpenChange(open: boolean) {
  emit("update:open", open)
}

watch(() => props.open, (active) => {
  if (active) {
    cycleGo(0)
  }
})
</script>

<template>
  <BaseModalReka
    :open="open"
    :title="currentSlide?.title ?? t('onboarding_guide_modal.welcome.title')"
    :description="currentSlide?.description ?? t('onboarding_guide_modal.welcome.description')"
    transition="flip-side"
    allow-overflow
    close-on-backdrop-click
    @update:open="onOpenChange"
  >
    <div class="relative mx-auto flex min-h-[85vh] w-[600px] max-w-[85vw] flex-col overflow-hidden rounded-xl border border-primary/10 bg-white md:min-h-0">
      <DialogClose as-child>
        <Button
          variant="flat"
          prefix-icon="Close"
          class="absolute right-5 top-5 z-10 flex h-11 items-center p-0 md:right-7 md:top-7"
          data-test="onboarding-guide-close"
        >
          <span class="sr-only">{{ t("freemium_welcome_modal.dismiss") }}</span>
        </Button>
      </DialogClose>

      <div class="flex min-h-0 min-w-0 flex-1 flex-col bg-white md:min-h-120">
        <div class="min-h-0 flex-1 overflow-y-auto px-5 pt-5 pb-5 md:px-7 md:pt-7 md:pb-7">
          <Transition name="gpx-fade" mode="out-in">
            <div
              v-if="currentSlide"
              :key="currentSlide.key"
              class="flex flex-col gap-4"
              data-test="onboarding-guide-slide"
            >
              <div class="flex items-center gap-4 pr-8">
                <div class="flex size-11 shrink-0 items-center justify-center rounded-xl bg-tertiary" aria-hidden="true">
                  <Icon :icon="currentSlide.icon" size="default" class="text-nav-active" />
                </div>

                <h2
                  class="font-heading text-lg font-bold leading-tight text-black md:text-xl"
                  data-test="onboarding-guide-slide-title"
                >
                  {{ currentSlide.title }}
                </h2>
              </div>

              <template v-if="currentSlide.bulletsLabel">
                <div v-if="currentSlide.bullets?.length" class="flex flex-col gap-2">
                  <p class="font-body text-sm font-medium text-black/70">
                    {{ currentSlide.bulletsLabel }}
                  </p>
                  <ul class="flex flex-col gap-2 font-body text-sm" data-test="onboarding-guide-slide-bullets">
                    <li
                      v-for="bullet in currentSlide.bullets"
                      :key="bullet"
                      class="flex items-start gap-2.5"
                    >
                      <Icon icon="Check" size="sm" class="mt-0.5 shrink-0 text-theme-success" />
                      <span class="text-black/85">{{ bullet }}</span>
                    </li>
                  </ul>
                </div>

                <p class="font-body text-sm leading-relaxed text-black/80" data-test="onboarding-guide-slide-description">
                  {{ currentSlide.description }}
                </p>
              </template>

              <template v-else>
                <p class="font-body text-sm leading-relaxed text-black/80" data-test="onboarding-guide-slide-description">
                  {{ currentSlide.description }}
                </p>

                <ul
                  v-if="currentSlide.bullets?.length"
                  class="flex flex-col gap-2 font-body text-sm mb-4"
                  data-test="onboarding-guide-slide-bullets"
                >
                  <li
                    v-for="bullet in currentSlide.bullets"
                    :key="bullet"
                    class="flex items-start gap-2.5"
                  >
                    <Icon icon="Check" size="sm" class="mt-0.5 shrink-0 text-theme-success" />
                    <span class="text-black/85">{{ bullet }}</span>
                  </li>
                </ul>

                <Alert v-if="currentSlide.tip" variant="info" prefix-icon="CircleInfo" :closable="false" data-test="onboarding-guide-slide-tip">
                  <span>{{ currentSlide.tip }}</span>
                </Alert>
              </template>

              <div v-if="currentSlide.showUpgradeCard && eventVault" class="flex flex-col gap-3">
                <FreemiumUpgradeCard class="hidden md:block" :upgrade-url="eventVault.upgradeUrl" />
                <FreemiumUpgradeCard variant="mini" class="block md:hidden" :upgrade-url="eventVault.upgradeUrl" />
              </div>
            </div>
          </Transition>
        </div>

        <div class="bg-white" data-test="onboarding-guide-controls">
          <div
            class="h-1 overflow-hidden bg-tertiary"
            role="progressbar"
            data-test="onboarding-guide-progress"
            :aria-valuenow="currentIndex + 1"
            aria-valuemin="1"
            :aria-valuemax="slides.length"
            :aria-label="stepAriaLabel"
          >
            <div
              class="h-full bg-primary transition-all duration-300 ease-out"
              :style="{ width: `${progressPercent}%` }"
            />
          </div>

          <div class="flex items-center justify-between gap-3 px-5 py-4 md:px-7 md:py-5">
            <p class="text-sm text-black/50" data-test="onboarding-guide-step-count">
              {{ stepLabel }}
            </p>

            <div class="flex items-center gap-2">
              <DialogClose v-if="isFirstSlide" as-child>
                <Button
                  variant="flat"
                  data-test="onboarding-guide-skip"
                >
                  {{ t("onboarding_guide_modal.skip") }}
                </Button>
              </DialogClose>
              <Button
                v-else
                variant="flat"
                data-test="onboarding-guide-back"
                @click="() => cyclePrev()"
              >
                {{ t("back") }}
              </Button>

              <Button
                data-test="onboarding-guide-primary-cta"
                @click="() => isLastSlide ? close() : cycleNext()"
              >
                {{ isLastSlide ? t("onboarding_guide_modal.got_it") : t("onboarding_guide_modal.next") }}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </BaseModalReka>
</template>
